import {
  type CmsMappedComponents,
  type CoreLayoutContentTypes,
} from '@/features/cms/components/CMS/types'
import dynamic from 'next/dynamic'
import { FullWidthBanner } from '@/features/cms/components/CMS/components/Shelves/FullWidthBanner'
import { StoresCarousel } from '@/features/cms/components/CMS/components/StoresCarousel'
import { getCMSShelfLoader } from '@/features/cms/components/CMS/CMSLoaders'
import { TakeoverBanner } from '@/features/cms/components/CMS/components/TakeoverBanner'
import { AdSkinnyShowcase } from '@/features/cms/components/CMS/components/Shelves/AdSkinnyShowcase'

const DynamicProductShelfCollection = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/ProductShelfCollection'))
      .ProductShelfCollection,
  { loading: () => getCMSShelfLoader('product_shelf_collection') },
)

const DynamicBentoBoxCollection = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/BentoBoxCollection'))
      .BentoBoxCollection,
  { loading: () => getCMSShelfLoader('bento_box_collection') },
)

const DynamicBentoBox = dynamic(
  async () => (await import('@/features/cms/components/CMS/components/BentoBox')).BentoBox,
  { loading: () => getCMSShelfLoader('bento_box') },
)
const DynamicAstroBanner = dynamic(
  async () => (await import('@/features/cms/components/CMS/components/AstroBanner')).AstroBanner,
  { loading: () => getCMSShelfLoader('astro_banner') },
)
const DynamicVideoBanner = dynamic(
  async () => (await import('@/features/cms/components/CMS/components/VideoBanner')).VideoBanner,
  { loading: () => getCMSShelfLoader('video_banner') },
)
const DynamicHeaderBanner = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/BrandAmplification/HeaderBanner'))
      .HeaderBanner,
  { loading: () => getCMSShelfLoader('header_banner') },
)

const DynamicPastOrdersShelf = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/PastOrdersShelf'))
      .PastOrdersShelf,
  { loading: () => getCMSShelfLoader('past_orders_shelf') },
)
const DynamicSmallNav = dynamic(
  async () => (await import('@/features/cms/components/CMS/components/Shelves/SmallNav')).SmallNav,
  { loading: () => getCMSShelfLoader('small_nav') },
)

const DynamicLargePromoCarousel = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/LargePromoCarousel'))
      .LargePromoCarousel,
  { loading: () => getCMSShelfLoader('large_promo_carousel') },
)

const DynamicCampaignPromoBanner = dynamic(
  async () =>
    (
      await import('@/features/cms/components/CMS/components/Shelves/CMSCampaignPromoBanner/CMSCampaignPromoBanner')
    ).CMSCampaignPromoBanner,
  { loading: () => getCMSShelfLoader('campaign_promo_banner') },
)
const DynamicMultiRetailerProducts = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/MultiRetailerProductShelves'))
      .MultiRetailerProductShelves,
  { loading: () => getCMSShelfLoader('multi_retailer_product_shelves') },
)
const DynamicHeroCardCarousel = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/HeroCardCarousel'))
      .HeroCardCarousel,
  { loading: () => getCMSShelfLoader('hero_card_carousel') },
)
const DynamicSmallPromoCarousel = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/SmallPromoCarousel'))
      .SmallPromoCarousel,
  { loading: () => getCMSShelfLoader('small_promo_carousel') },
)
const DynamicCMSCouponShelf = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/CMSCouponShelf'))
      .CMSCouponShelf,
  { loading: () => getCMSShelfLoader('coupon_shelf') },
)
const DynamicCMSProductShelf = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/ProductShelf/CMSProductShelf'))
      .CMSProductShelf,
  { loading: () => getCMSShelfLoader('product_shelf') },
)
const DynamicCMSUniversalProductShelf = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/ProductShelf/CMSProductShelf'))
      .CMSUniversalProductShelf,
  { loading: () => getCMSShelfLoader('universal_product_shelf') },
)
const DynamicStandardNav = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/StandardNav')).StandardNav,
  { loading: () => getCMSShelfLoader('standard_nav') },
)
const DynamicAdBanner = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/CMSAdBanner')).AdBanner,
  { loading: () => getCMSShelfLoader('ad_banner') },
)

const DynamicEnhancedNav = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/EnhancedNav')).EnhancedNav,
  { loading: () => getCMSShelfLoader('enhanced_nav') },
)
const DynamicEnhancedProduct = dynamic(
  async () =>
    (await import('@/features/cms/components/CMS/components/Shelves/EnhancedProduct'))
      .EnhancedProduct,
  { loading: () => getCMSShelfLoader('enhanced_product') },
)
const DynamicPunchCard = dynamic(
  async () => (await import('@/features/cms/components/CMS/components/PunchCard')).PunchCard,
  { loading: () => getCMSShelfLoader('punch_card') },
)

export const coreLayoutCmsComponents: CmsMappedComponents<
  CoreLayoutContentTypes['content_type_id']
> = {
  product_shelf_collection: DynamicProductShelfCollection,
  video_banner: DynamicVideoBanner,
  bento_box_collection: DynamicBentoBoxCollection,
  bento_box: DynamicBentoBox,
  astro_banner: DynamicAstroBanner,
  header_banner: DynamicHeaderBanner,
  ad_banner: DynamicAdBanner,
  campaign_promo_banner: DynamicCampaignPromoBanner,
  coupon_shelf: DynamicCMSCouponShelf,
  enhanced_nav: DynamicEnhancedNav,
  enhanced_product: DynamicEnhancedProduct,
  full_width_banner: FullWidthBanner,
  hero_card_carousel: DynamicHeroCardCarousel,
  large_promo_carousel: DynamicLargePromoCarousel,
  multi_retailer_product_shelves: DynamicMultiRetailerProducts,
  past_orders_shelf: DynamicPastOrdersShelf,
  product_shelf: DynamicCMSProductShelf,
  small_nav: DynamicSmallNav,
  small_promo_carousel: DynamicSmallPromoCarousel,
  standard_nav: DynamicStandardNav,
  stores_carousel: StoresCarousel,
  takeover_banner: TakeoverBanner,
  punch_card: DynamicPunchCard,
  ad_showcase: AdSkinnyShowcase,
  universal_product_shelf: DynamicCMSUniversalProductShelf,
}
